import { usePendingInstallScheduling } from '@cobliteam/ai/src/hooks/usePendingInstallScheduling';
import { useNudge } from '@cobliteam/hooks/src/useNudge';
import type { Nudge, NudgeSurface } from '@cobliteam/hooks/src/useNudge/types';
import { useTranslation } from '@cobliteam/i18n';
import NudgeMessage from '@cobliteam/ui-business/components/Nudge/NudgeMessage';

export const SCHEDULE_INSTALLATION_NUDGE_ID = 'schedule-installation';

/**
 * Suggestion, not recommendation: it is a deterministic read of the fleet's open CRM tickets, with
 * no AI workflow deciding whether to show it. The scheduling agent enters after the click — it is
 * not what produced the suggestion, which is why this needs no `onExpand` deferral.
 *
 * This used to come from the recommendations agent (the `installation` journey) until AE-2574
 * removed the basic-value recommendations. Eligibility and the agent hand-off live in
 * `usePendingInstallScheduling`, shared with the option in the floating action button.
 */
export const useScheduleInstallationNudge = (surface: NudgeSurface = 'floating-card'): Nudge | null => {
  const { t } = useTranslation('suggestions');
  const scheduling = usePendingInstallScheduling();

  const { shouldShow, dismiss, accept } = useNudge({
    id: SCHEDULE_INSTALLATION_NUDGE_ID,
    kind: 'suggestion',
    surface,
    useShouldShow: () => scheduling.isAvailable,
  });

  if (!shouldShow) return null;

  const title = t('schedule_installation.title', { count: scheduling.pendingCount });

  return {
    id: SCHEDULE_INSTALLATION_NUDGE_ID,
    kind: 'suggestion',
    priority: 0,
    tagLabel: t('schedule_installation.tag_label'),
    title,
    dismiss,
    render: () => (
      <NudgeMessage
        title={title}
        description={t('schedule_installation.description')}
        ctaLabel={t('schedule_installation.cta')}
        onCta={() => {
          // `accept` already emits `Suggestion Accepted` with suggestionId, kind and surface, so the
          // extra bespoke event this used to fire was a second event for one click, missing exactly
          // the segmentation the shared one carries. Its only unique field rides along here.
          accept({ pendingCount: scheduling.pendingCount, scoped: scheduling.isScoped });
          scheduling.open('nudge');
        }}
        dismissLabel={t('schedule_installation.not_interested')}
        onDismiss={() => dismiss('not-interested')}
      />
    ),
  };
};

export default useScheduleInstallationNudge;
